type RecordingState = 'idle' | 'recording' | 'uploading';

export interface AudioSegment {
  id: string;
  url: string;
  duration: number;  // seconds
  createdAt: number; // timestamp
}

interface AudioRecorderOptions {
  diagramId: string;
  onStateChange?: (state: RecordingState) => void;
  onTimeUpdate?: (seconds: number) => void;
  onSegmentReady?: (segment: AudioSegment) => void;
  onError?: (error: string) => void;
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private state: RecordingState = 'idle';
  private startTime: number = 0;
  private timerInterval: number | null = null;
  private diagramId: string;

  // Callbacks
  private onStateChange?: (state: RecordingState) => void;
  private onTimeUpdate?: (seconds: number) => void;
  private onSegmentReady?: (segment: AudioSegment) => void;
  private onError?: (error: string) => void;

  constructor(options: AudioRecorderOptions) {
    this.diagramId = options.diagramId;
    this.onStateChange = options.onStateChange;
    this.onTimeUpdate = options.onTimeUpdate;
    this.onSegmentReady = options.onSegmentReady;
    this.onError = options.onError;
  }

  async startRecording(): Promise<boolean> {
    if (this.state !== 'idle') {
      return false;
    }

    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      // Create MediaRecorder with webm format (best browser support)
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.handleRecordingComplete();
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        this.onError?.('Recording failed');
        this.cleanup();
      };

      // Start recording
      this.mediaRecorder.start(1000); // Capture in 1-second chunks
      this.state = 'recording';
      this.startTime = Date.now();
      this.onStateChange?.('recording');

      // Start timer
      this.timerInterval = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
        this.onTimeUpdate?.(elapsed);
      }, 100);

      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        this.onError?.('Microphone access denied');
      } else {
        this.onError?.('Failed to access microphone');
      }
      return false;
    }
  }

  stopRecording(): void {
    if (this.state !== 'recording' || !this.mediaRecorder) {
      return;
    }

    // Stop the timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    // Stop recording (triggers onstop handler)
    this.mediaRecorder.stop();
  }

  private async handleRecordingComplete(): Promise<void> {
    const duration = Math.floor((Date.now() - this.startTime) / 1000);

    this.state = 'uploading';
    this.onStateChange?.('uploading');

    // Stop all tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Create blob from chunks
    const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });

    if (audioBlob.size === 0) {
      this.onError?.('No audio recorded');
      this.cleanup();
      return;
    }

    // Generate segment ID
    const segmentId = `seg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Upload to server
    try {
      const response = await fetch(`/api/audio/${this.diagramId}/${segmentId}`, {
        method: 'POST',
        body: audioBlob,
        headers: {
          'Content-Type': 'audio/webm',
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();

      const segment: AudioSegment = {
        id: segmentId,
        url: result.url,
        duration,
        createdAt: Date.now(),
      };

      this.onSegmentReady?.(segment);
    } catch (error) {
      console.error('Upload error:', error);
      this.onError?.('Failed to save recording');
    }

    this.cleanup();
  }

  private cleanup(): void {
    this.state = 'idle';
    this.onStateChange?.('idle');
    this.mediaRecorder = null;
    this.audioChunks = [];

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  cancelRecording(): void {
    if (this.state !== 'recording') {
      return;
    }

    // Stop timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    // Stop media recorder without triggering upload
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.onstop = null; // Prevent upload
      this.mediaRecorder.stop();
    }

    // Stop all tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    this.cleanup();
  }

  getState(): RecordingState {
    return this.state;
  }

  isRecording(): boolean {
    return this.state === 'recording';
  }

  // Static method to delete audio segment from server
  static async deleteSegment(diagramId: string, segmentId: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/audio/${diagramId}/${segmentId}`, {
        method: 'DELETE',
      });
      return response.ok;
    } catch (error) {
      console.error('Failed to delete audio segment:', error);
      return false;
    }
  }
}

// Audio player for sequential segment playback
export class SegmentPlayer {
  private audio: HTMLAudioElement | null = null;
  private segments: AudioSegment[] = [];
  private currentIndex: number = 0;
  private isPlaying: boolean = false;

  private onPlayStateChange?: (playing: boolean) => void;
  private onTimeUpdate?: (currentTime: number, totalDuration: number, segmentIndex: number) => void;
  private onSegmentChange?: (index: number) => void;
  private onEnded?: () => void;

  constructor(options?: {
    onPlayStateChange?: (playing: boolean) => void;
    onTimeUpdate?: (currentTime: number, totalDuration: number, segmentIndex: number) => void;
    onSegmentChange?: (index: number) => void;
    onEnded?: () => void;
  }) {
    this.onPlayStateChange = options?.onPlayStateChange;
    this.onTimeUpdate = options?.onTimeUpdate;
    this.onSegmentChange = options?.onSegmentChange;
    this.onEnded = options?.onEnded;
  }

  setSegments(segments: AudioSegment[]): void {
    this.segments = segments;
    this.currentIndex = 0;
    if (this.audio) {
      this.audio.pause();
    }
    this.isPlaying = false;
  }

  private loadSegment(index: number): void {
    if (index < 0 || index >= this.segments.length) return;

    const segment = this.segments[index];
    this.currentIndex = index;

    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
    }

    this.audio = new Audio(segment.url);

    this.audio.addEventListener('ended', () => {
      // Move to next segment
      if (this.currentIndex < this.segments.length - 1) {
        this.loadSegment(this.currentIndex + 1);
        this.audio?.play();
        this.onSegmentChange?.(this.currentIndex);
      } else {
        // All segments complete
        this.isPlaying = false;
        this.currentIndex = 0;
        this.onPlayStateChange?.(false);
        this.onEnded?.();
      }
    });

    this.audio.addEventListener('timeupdate', () => {
      if (this.audio) {
        const elapsed = this.getElapsedTime() + this.audio.currentTime;
        this.onTimeUpdate?.(elapsed, this.getTotalDuration(), this.currentIndex);
      }
    });
  }

  play(): void {
    if (this.segments.length === 0) return;

    if (!this.audio) {
      this.loadSegment(0);
    }

    this.audio?.play();
    this.isPlaying = true;
    this.onPlayStateChange?.(true);
  }

  pause(): void {
    this.audio?.pause();
    this.isPlaying = false;
    this.onPlayStateChange?.(false);
  }

  toggle(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.isPlaying = false;
    this.currentIndex = 0;
    this.onPlayStateChange?.(false);
  }

  seekToSegment(index: number): void {
    if (index < 0 || index >= this.segments.length) return;

    const wasPlaying = this.isPlaying;
    this.loadSegment(index);
    this.onSegmentChange?.(index);

    if (wasPlaying) {
      this.audio?.play();
    }
  }

  getIsPlaying(): boolean {
    return this.isPlaying;
  }

  getTotalDuration(): number {
    return this.segments.reduce((sum, s) => sum + s.duration, 0);
  }

  private getElapsedTime(): number {
    let elapsed = 0;
    for (let i = 0; i < this.currentIndex; i++) {
      elapsed += this.segments[i].duration;
    }
    return elapsed;
  }

  getCurrentSegmentIndex(): number {
    return this.currentIndex;
  }

  destroy(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = null;
    }
    this.segments = [];
    this.isPlaying = false;
  }
}
